import { IamAccount, IamTenant } from '@apps/iam';
import { EChartsOption } from 'echarts/types/dist/shared';

export enum DigitaiAlertLogCriticality
{
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
}

export enum DigitaiAlertLogStatus
{
    CONFIRMED = 'CONFIRMED',
    DISMISSED = 'DISMISSED',
    PENDING = 'PENDING',
    NEEDS_REVIEW = 'NEEDS_REVIEW',
}

export enum DigitaiAlertLogConfirmedType
{
    FIRST_ALERT = 'FIRST_ALERT',
    RECURRING_ALERT = 'RECURRING_ALERT',
    PROMOTION_ENGINE_EVENT = 'PROMOTION_ENGINE_EVENT',
}

export enum DigitaiAlertLogReasonType
{
    CONFIRMED = 'CONFIRMED',
    DISMISSED = 'DISMISSED',
}

export enum DigitaiTrackedIssueType
{
    DAILY = 'DAILY',
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
    QUARTERLY = 'QUARTERLY',
}

export interface DigitaiCompany
{
    id: string;
    tenant: IamTenant;
    username: string;
    password: string;
    hasFileManagerChatbot: boolean;
    hasSqlChatbot: boolean;
}

export interface DigitaiTab
{
    id: string;
    name: string;
    type: DigitaiTabType;
    link?: string;
    sort: number;
    isActive: boolean;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export enum DigitaiTabType
{
    OVERVIEW = 'OVERVIEW',
    FLEET = 'FLEET',
    ISSUES = 'ISSUES',
    AI = 'AI',
}

export enum DigitChatGroup
{
    FILE_MANAGER_CHAT = 'FILE_MANAGER_CHAT',
    GENERAL_CHAT = 'GENERAL_CHAT',
    ISSUE_CHAT = 'ISSUE_CHAT',
    SUPPORT_CHAT = 'SUPPORT_CHAT',
}

export enum DigitIamAccountScope
{
    OPERATOR = 'OPERATOR',
}

export interface DigitaiCreateTab
{
    id: string;
    name: string;
    type: string;
    link?: string;
    sort: number;
    isActive: boolean;
}

export interface DigitaiUpdateTabById
{
    id: string;
    name?: string;
    type?: string;
    link?: string;
    sort?: number;
    isActive?: boolean;
}

export interface DigitaiUpdateTabs
{
    id?: string;
    name?: string;
    type?: string;
    link?: string;
    sort?: number;
    isActive?: boolean;
}

export interface DigitaiPotentialIssue
{
    id: string;
    name: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreatePotentialIssue
{
    id: string;
    name: string;
}

export interface DigitaiUpdatePotentialIssueById
{
    id: string;
    name?: string;
}

export interface DigitaiUpdatePotentialIssues
{
    id?: string;
    name?: string;
}

export interface DigitaiAircraftStatus
{
    id: string;
    name: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateAircraftStatus
{
    id: string;
    name: string;
}

export interface DigitaiUpdateAircraftStatusById
{
    id: string;
    name?: string;
}

export interface DigitaiUpdateAircraftStatuses
{
    id?: string;
    name?: string;
}

export interface DigitaiIssueEngineStatus
{
    id: string;
    name: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateIssueEngineStatus
{
    id: string;
    name: string;
}

export interface DigitaiUpdateIssueEngineStatusById
{
    id: string;
    name?: string;
}

export interface DigitaiUpdateIssueEngineStatuses
{
    id?: string;
    name?: string;
}

export enum DigitaiIssueStatus
{
    OPEN_ISSUE = 'OPEN_ISSUE',
    IN_REVIEW = 'IN_REVIEW',
    CLOSED = 'CLOSED',
}

export enum DigitaiIssueRequestStatus
{
    PENDING = 'PENDING',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
}

export interface DigitaiAircraftModel
{
    id: string;
    name: string;
    totalMotors: number;
    aircraftStatuses?: DigitaiAircraftStatus[];
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateAircraftModel
{
    id: string;
    name: string;
    totalMotors: number;
    aircraftStatuses?: string[];
}

export interface DigitaiUpdateAircraftModelById
{
    id: string;
    name?: string;
    totalMotors?: number;
    aircraftStatuses?: DigitaiAircraftStatus[];
}

export interface DigitaiUpdateAircraftModels
{
    id?: string;
    name?: string;
    totalMotors?: number;
    aircraftStatuses?: string[];
}

export interface DigitaiEngineModel
{
    id: string;
    name: string;
    potentialIssues?: DigitaiPotentialIssue[];
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateEngineModel
{
    id: string;
    name: string;
    potentialIssues?: string[];
}

export interface DigitaiUpdateEngineModelById
{
    id: string;
    name?: string;
    potentialIssues?: DigitaiPotentialIssue[];
}

export interface DigitaiUpdateEngineModels
{
    id?: string;
    name?: string;
    potentialIssues?: string[];
}

export interface DigitaiFleet
{
    id: string;
    tenantIds: string[];
    companyId?: string;
    company: DigitaiCompany;
    aircraftModelId: string;
    aircraftModel?: DigitaiAircraftModel;
    engineModelId: string;
    engineModel: DigitaiEngineModel;
    hasOpenIssues: boolean;
    hasAiPredictions: boolean;
    hasSchedule: boolean;
    fleetTabs: DigitaiFleetTab[];
    aircrafts?: DigitaiFleetAircraft[];
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateFleet
{
    id: string;
    tenantId: string;
    aircraftModelId: string;
    aircraftModelName: string;
    engineModelId: string;
    engineModelName: string;
    hasOpenIssues: boolean;
    hasAiPredictions: boolean;
    hasSchedule: boolean;
    fleetTabs: DigitaiFleetTab[];
    aircrafts?: DigitaiFleetAircraft[];
}

export interface DigitaiUpdateFleetById
{
    id: string;
    tenantId?: string;
    aircraftModelId?: string;
    aircraftModelName?: string;
    engineModelId?: string;
    engineModelName?: string;
    hasOpenIssues?: boolean;
    hasAiPredictions?: boolean;
    hasSchedule?: boolean;
    fleetTabs?: DigitaiFleetTab[];
    aircrafts?: DigitaiFleetAircraft[];
}

export interface DigitaiUpdateFleets
{
    id?: string;
    tenantId?: string;
    aircraftModelId?: string;
    aircraftModelName?: string;
    engineModelId?: string;
    engineModelName?: string;
    hasOpenIssues?: boolean;
    hasAiPredictions?: boolean;
    hasSchedule?: boolean;
    fleetTabs?: DigitaiFleetTab[];
    aircrafts?: DigitaiFleetAircraft[];
}

export interface DigitaiFleetTab
{
    id: string;
    tenantId: string;
    fleetId: string;
    name: string;
    type: string;
    link?: string;
    sort: number;
    isMaster: boolean;
    isActive: boolean;

    // for charts
    title?: string;
    chartTypeId?: string;
    chartType?: DigitaiChartType;
    enginesJoin?: boolean;

    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiFleetTabMenuItem
{
    id: string;
    name: string;
    type?: string;
    link?: string;
}

export enum DigitaiFleetTabType
{
    AI = 'AI',
    FLEET = 'FLEET',
    ISSUES = 'ISSUES',
    LINK = 'LINK',
    OVERVIEW = 'OVERVIEW',
    OVERVIEW_GRAPH = 'OVERVIEW_GRAPH',
    EMBED_URL = 'EMBED_URL',
    EXTERNAL_LINK = 'EXTERNAL_LINK',
}

export interface DigitaiCreateFleetTab
{
    id: string;
    tenantId: string;
    fleetId: string;
    name: string;
    type: string;
    link?: string;
    sort: number;
    isMaster: boolean;
    isActive: boolean;
}

export interface DigitaiUpdateFleetTabById
{
    id: string;
    tenantId?: string;
    fleetId?: string;
    name?: string;
    type?: string;
    link?: string;
    sort?: number;
    isMaster?: boolean;
    isActive?: boolean;
}

export interface DigitaiUpdateFleetTabs
{
    id?: string;
    tenantId?: string;
    fleetId?: string;
    name?: string;
    type?: string;
    link?: string;
    sort?: number;
    isMaster?: boolean;
    isActive?: boolean;
}

export interface DigitaiFleetAircraft
{
    id: string;
    tenantIds: string[];
    fleetId: string;
    fleet: DigitaiFleet;
    aircraftModelId: string;
    aircraftModel: DigitaiAircraftModel;
    plate: string;
    serialNumber?: string;
    statusId: string;
    status?: DigitaiAircraftStatus;
    engines?: DigitaiFleetAircraftEngine[];
    engine1Id?: string;
    engine1SerialNumber?: string;
    engine2Id?: string;
    engine2SerialNumber?: string;
    engine3Id?: string;
    engine3SerialNumber?: string;
    engine4Id?: string;
    engine4SerialNumber?: string;
    meta?: {
        dynamicConfigurations?: { [key: string]: any };
    }
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateFleetAircraft
{
    id: string;
    tenantId: string;
    fleetId: string;
    aircraftModelId: string;
    plate: string;
    serialNumber?: string;
    statusId: string;
    engines?: DigitaiFleetAircraftEngine[];
}

export interface DigitaiUpdateFleetAircraftById
{
    id: string;
    tenantId?: string;
    fleetId?: string;
    aircraftModelId?: string;
    plate?: string;
    serialNumber?: string;
    statusId?: string;
    engines?: DigitaiFleetAircraftEngine[];
}

export interface DigitaiUpdateFleetAircrafts
{
    id?: string;
    tenantId?: string;
    fleetId?: string;
    aircraftModelId?: string;
    plate?: string;
    serialNumber?: string;
    statusId?: string;
    engines?: DigitaiFleetAircraftEngine[];
}

export interface DigitaiFleetAircraftEngine
{
    id: string;
    tenantIds: string[];
    companyId: string;
    company?: DigitaiCompany;
    fleetId: string;
    status: DigitaiFleetAircraftEngineStatus;
    engineModelId: string;
    engineModel?: DigitaiEngineModel;
    fleetAircraftId?: string;
    fleetAircraft?: DigitaiFleetAircraft;
    position?: number;
    serialNumber: string;
    operationDate?: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateFleetAircraftEngine
{
    id: string;
    tenantId: string;
    engineModelId: string;
    fleetAircraftId: string;
    position?: number;
    serialNumber: string;
    operationDate?: string;
}

export interface DigitaiUpdateFleetAircraftEngineById
{
    id: string;
    tenantIds?: string[];
    companyId?: string;
    fleetId?: string;
    status?: DigitaiFleetAircraftEngineStatus;
    engineModelId?: string;
    engineFlyingHours?: number;
    fleetAircraftId?: string;
    position?: number;
    serialNumber?: string;
    operationDate?: string;
}

export interface DigitaiUpdateFleetAircraftEngines
{
    id?: string;
    tenantId?: string;
    engineModelId?: string;
    fleetAircraftId?: string;
    position?: number;
    serialNumber?: string;
}

export enum DigitaiFleetAircraftEngineStatus
{
    INSTALL = 'INSTALL',
    POOL = 'POOL',
}

// Enum para el tipo de acción en el historial
export enum DigitaiEngineActionType
{
    INSTALLED = 'INSTALLED',
    REMOVED = 'REMOVED',
}

// Modelo del historial de acciones de motor
export interface DigitaiEngineActionHistory
{
    id: string;
    fleetAircraftEngineId: string;
    date: string;
    type: DigitaiEngineActionType;
    location?: string;
    engineFlyingHours?: number;
}

export interface DigitaiIssueRequest
{
    id: string;
    tenantIds: string[];
    companyId: string;
    company?: DigitaiCompany;
    fleetId: string;
    fleet?: DigitaiFleet;
    title: string;
    aircraftModelId: string;
    aircraftModel?: DigitaiAircraftModel;
    engineModelId: string;
    engineModel: DigitaiEngineModel;
    fleetAircraftId?: string;
    fleetAircraft?: DigitaiFleetAircraft;
    fleetAircraftPlate: string;
    fleetAircraftEngineId: string;
    fleetAircraftEngine?: DigitaiFleetAircraftEngine;
    fleetAircraftEngineSerialNumber: string;
    fleetAircraftEnginePosition?: number;
    status: DigitaiIssueRequestStatus;
    potentialIssueId: string;
    potentialIssue?: DigitaiPotentialIssue;
    customerComments?: string;
    issueDateAt: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateIssueRequest
{
    id: string;
    tenantIds: string[];
    fleetId: string;
    aircraftModelId: string;
    aircraftModelName: string;
    engineModelId: string;
    engineModelName: string;
    fleetAircraftId?: string;
    fleetAircraftEngineId?: string;
    fleetAircraftPlate: string;
    fleetAircraftEngineSerialNumber: string;
    fleetAircraftEnginePosition: number;
    potentialIssueId: string;
    companyName: string;
    issueDateAt: string;
}

export interface DigitaiUpdateIssueRequestById
{
    id: string;
    tenantIds?: string[];
    fleetId?: string;
    aircraftModelId?: string;
    aircraftModelName?: string;
    engineModelId?: string;
    engineModelName?: string;
    fleetAircraftId?: string;
    fleetAircraftEngineId?: string;
    fleetAircraftPlate?: string;
    fleetAircraftEngineSerialNumber?: string;
    fleetAircraftEnginePosition?: number;
    status: DigitaiIssueRequestStatus;
    potentialIssueId?: string;
    companyName?: string;
    issueDateAt?: string;
}

export interface DigitaiUpdateIssueRequests
{
    id?: string;
    tenantIds?: string[];
    fleetId?: string;
    aircraftModelId?: string;
    aircraftModelName?: string;
    engineModelId?: string;
    engineModelName?: string;
    fleetAircraftId?: string;
    fleetAircraftEngineId?: string;
    fleetAircraftPlate?: string;
    fleetAircraftEngineSerialNumber?: string;
    fleetAircraftEnginePosition?: number;
    potentialIssueId?: string;
    companyName?: string;
    issueDateAt?: string;
}

export interface DigitaiIssue
{
    id: string;
    code: string;
    tenantIds: string[];
    chartType?: DigitaiChartType;
    chartVariables?: DigitaiChartVariable[];
    chartStartDate?: string;
    chartEndDate?: string;
    companyId: string;
    company?: DigitaiCompany;
    fleetId: string;
    fleet?: DigitaiFleet;
    title: string;
    aircraftModelId: string;
    aircraftModel?: DigitaiAircraftModel;
    engineModelId: string;
    engineModel: DigitaiEngineModel;
    fleetAircraftId?: string;
    fleetAircraft?: DigitaiFleetAircraft;
    fleetAircraftPlate: string;
    fleetAircraftEngineId: string;
    fleetAircraftEngine?: DigitaiFleetAircraftEngine;
    fleetAircraftEngineSerialNumber: string;
    fleetAircraftEnginePosition?: number;
    status: DigitaiIssueStatus;
    potentialIssueId: string;
    potentialIssue?: DigitaiPotentialIssue;
    issueDateAt: string;
    issueDateClosedAt: string;
    issueDateDaysOpen: number;
    engineStatusId: string;
    engineStatus: DigitaiIssueEngineStatus;
    identifiedRootCauseId?: string;
    identifiedRootCause?: DigitaiIdentifiedRootCause;
    conversations?: DigitaiIssueConversation[];
    technicalComments: string;
    images?: any;
    attachments?: any;
    lastTrackedIssueUpdatedAt?: string;
    lastTrackedIssueFollowUpFrequency?: DigitaiTrackedIssueType;
    lastTrackedIssueFollowUpAt?: string;
    showChartInReport?: boolean;
    fixAndValuations?: DigitaiFixAndValuation[];
    trackedIssues?: DigitaiTrackedIssue[];
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export enum DigitaiChartModule
{
    OVERVIEW = 'OVERVIEW',
    ISSUE = 'ISSUE',
}

export interface DigitaiGetOverviewChartFromAPI
{
    chartType: string;
    title: string;
    enginesJoin: boolean;

    aircraftModelName: string;
    clientName: string;
    module: DigitaiChartModule;
}

export interface DigitaiCreateIssue
{
    id: string;
    tenantIds: string[];
    fleetId: string;
    aircraftModelId: string;
    aircraftModelName: string;
    engineModelId: string;
    engineModelName: string;
    fleetAircraftId?: string;
    fleetAircraftEngineId?: string;
    fleetAircraftPlate: string;
    fleetAircraftEngineSerialNumber: string;
    fleetAircraftEnginePosition: number;
    status: string;
    potentialIssueId: string;
    companyName: string;
    issueDateAt: string;
    engineStatusId: string;
    conversations?: DigitaiIssueConversation[];
    technicalComments: string;
    images?: any;
    attachments?: any;
}

export interface DigitaiUpdateIssueById
{
    id: string;
    tenantIds?: string[];
    fleetId?: string;
    aircraftModelId?: string;
    aircraftModelName?: string;
    engineModelId?: string;
    engineModelName?: string;
    fleetAircraftId?: string;
    fleetAircraftEngineId?: string;
    fleetAircraftPlate?: string;
    fleetAircraftEngineSerialNumber?: string;
    fleetAircraftEnginePosition?: number;
    status?: string;
    potentialIssueId?: string;
    companyName?: string;
    issueDateAt?: string;
    engineStatusId?: string;
    conversations?: DigitaiIssueConversation[];
    technicalComments?: string;
    images?: any;
    attachments?: any;
}

export interface DigitaiUpdateIssues
{
    id?: string;
    tenantIds?: string[];
    fleetId?: string;
    aircraftModelId?: string;
    aircraftModelName?: string;
    engineModelId?: string;
    engineModelName?: string;
    fleetAircraftId?: string;
    fleetAircraftEngineId?: string;
    fleetAircraftPlate?: string;
    fleetAircraftEngineSerialNumber?: string;
    fleetAircraftEnginePosition?: number;
    status?: string;
    potentialIssueId?: string;
    companyName?: string;
    issueDateAt?: string;
    engineStatusId?: string;
    conversations?: DigitaiIssueConversation[];
    technicalComments?: string;
    images?: any;
    attachments?: any;
}

export interface DigitaiIssueConversation
{
    id: string;
    tenantIds: string[];
    accountId: string;
    account: IamAccount;
    issueId: string;
    issue: DigitaiIssue;
    message: string;
    isRead: boolean;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateIssueConversation
{
    id: string;
    tenantIds: string[];
    accountId: string;
    issueId: string;
    message: string;
}

export interface DigitaiUpdateIssueConversationById
{
    id: string;
    tenantId?: string;
    accountId?: string;
    accountUserName?: string;
    accountUserSurname?: string;
    accountUserAvatar?: any;
    issueId?: string;
    conversation?: string;
}

export interface DigitaiUpdateIssueConversations
{
    id?: string;
    tenantId?: string;
    accountId?: string;
    accountUserName?: string;
    accountUserSurname?: string;
    accountUserAvatar?: any;
    issueId?: string;
    conversation?: string;
}

export interface DigitaiEngineModelPotentialIssue
{
    engineModelId: string;
    potentialIssueId: string;
    engineModel?: DigitaiEngineModel;
    potentialIssue?: DigitaiPotentialIssue;
}

export interface DigitaiEngineModelPotentialIssue
{
    engineModelId: string;
    potentialIssueId: string;
    engineModel?: DigitaiEngineModel;
    potentialIssue?: DigitaiPotentialIssue;
}

export interface DigitaiCreateEngineModelPotentialIssue
{
    engineModelId: string;
    potentialIssueId: string;
}

export interface DigitaiUpdateEngineModelPotentialIssueById
{
    engineModelId?: string;
    potentialIssueId?: string;
}

export interface DigitaiUpdateEngineModelsPotentialIssues
{
    engineModelId?: string;
    potentialIssueId?: string;
}

export interface DigitaiAircraftModelAircraftStatus
{
    aircraftModelId: string;
    aircraftModel: DigitaiAircraftModel;
    aircraftStatusId: string;
    aircraftStatus: DigitaiAircraftStatus;
}

export interface DigitaiCreateAircraftModelAircraftStatus
{
    aircraftModelId: string;
    aircraftStatusId: string;
}

export interface DigitaiUpdateAircraftModelAircraftStatusById
{
    aircraftModelId?: string;
    aircraftStatusId?: string;
}

export interface DigitaiUpdateAircraftModelsAircraftStatuses
{
    aircraftModelId?: string;
    aircraftStatusId?: string;
}

export interface DigitaiCreateCompany
{
    id: string;
    name: string;
    tenantId?: string;
}

export interface DigitaiUpdateCompanyById
{
    id: string;
    name?: string;
    tenantId?: string;
}

export interface DigitaiUpdateCompanies
{
    id?: string;
    name?: string;
    tenantId?: string;
}

export interface DigitaiOverviewData
{
    digitaiGetIssues: DigitaiIssue[];
    digitaiGetAions: DigitaiAion[];
}

export interface DigitaiAlertLog
{
    id: string;
    tenantIds;
    companyId: string;
    fleetId: string;
    aircraftModelId: string;
    fleetAircraftId: string;
    issueId?: string;
    issue?: DigitaiIssue;
    alertAt: string;
    description: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateAlertLog
{
    id: string;
    tenantIds: string[];
    companyId: string;
    fleetId: string;
    aircraftModelId: string;
    fleetAircraftId: string;
    alertAt: string;
    description: string;
}

export interface DigitaiUpdateAlertLogById
{
    id: string;
    tenantIds?;
    companyId?: string;
    fleetId?: string;
    aircraftModelId?: string;
    fleetAircraftId?: string;
    alertAt?: string;
    description?: string;
}

export interface DigitaiUpdateAlertLogs
{
    id?: string;
    tenantIds?: string[];
    companyId?: string;
    fleetId?: string;
    aircraftModelId?: string;
    fleetAircraftId?: string;
    alertAt?: string;
    description?: string;
}

export interface DigitaiAion
{
    id: string;
    companyId: string;
    fleetId: string;
    aircraftModelId: string;
    engineModelId: string;
    fleetAircraftId: string;
    fleetAircraft?: DigitaiFleetAircraft;
    fleetAircraftEngineId: string;
    fleetAircraftEngine: DigitaiFleetAircraftEngine;
    potentialIssueId: string;
    potentialIssue?: DigitaiPotentialIssue;
    potentialIssueAlert1?: string;
    potentialIssueAlert2?: string;
    potentialIssueAlert3?: string;
    registerAt?: string;
    datetimeProcessAt?: string;
    actualValue?: number;
    iaValue1?: number;
    iaValue2?: number;
    iaValue3?: number;
    iaWorst1?: number;
    iaWorst2?: number;
    iaWorst3?: number;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateAion
{
    id: string;
    companyId: string;
    fleetId: string;
    aircraftModelId: string;
    engineModelId: string;
    fleetAircraftId: string;
    fleetAircraftEngineId: string;
    potentialIssueId: string;
    potentialIssueAlert1?: string;
    potentialIssueAlert2?: string;
    potentialIssueAlert3?: string;
    registerAt?: string;
    datetimeProcessAt?: string;
    actualValue?: number;
    iaValue1?: number;
    iaValue2?: number;
    iaValue3?: number;
    iaWorst1?: number;
    iaWorst2?: number;
    iaWorst3?: number;
}

export interface DigitaiUpdateAionById
{
    id: string;
    companyId?: string;
    fleetId?: string;
    aircraftModelId?: string;
    engineModelId?: string;
    fleetAircraftId?: string;
    fleetAircraftEngineId?: string;
    potentialIssueId?: string;
    potentialIssueAlert1?: string;
    potentialIssueAlert2?: string;
    potentialIssueAlert3?: string;
    registerAt?: string;
    datetimeProcessAt?: string;
    actualValue?: number;
    iaValue1?: number;
    iaValue2?: number;
    iaValue3?: number;
    iaWorst1?: number;
    iaWorst2?: number;
    iaWorst3?: number;
}

export interface DigitaiUpdateAions
{
    id?: string;
    companyId?: string;
    fleetId?: string;
    aircraftModelId?: string;
    engineModelId?: string;
    fleetAircraftId?: string;
    fleetAircraftEngineId?: string;
    potentialIssueId?: string;
    potentialIssueAlert1?: string;
    potentialIssueAlert2?: string;
    potentialIssueAlert3?: string;
    registerAt?: string;
    datetimeProcessAt?: string;
    actualValue?: number;
    iaValue1?: number;
    iaValue2?: number;
    iaValue3?: number;
    iaWorst1?: number;
    iaWorst2?: number;
    iaWorst3?: number;
}

export interface DigitaiOperator
{
    id: string;
    accountId: string;
    companyId: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateOperator
{
    id: string;
    accountId: string;
    companyId: string;
}

export interface DigitaiUpdateOperatorById
{
    id: string;
    accountId?: string;
    companyId?: string;
}

export interface DigitaiUpdateOperators
{
    id?: string;
    accountId?: string;
    companyId?: string;
}

export interface DigitaiFleetFile
{
    id: string;
    companyId: string;
    fleetId: string;
    accountId: string;
    folderId?: string;
    type: string;
    originName: string;
    name: string;
    mimetype?: string;
    extension?: string;
    relativePathSegments;
    width?: number;
    height?: number;
    size?: number;
    url?: string;
    meta?: any;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateFleetFile
{
    id: string;
    companyId: string;
    fleetId: string;
    accountId: string;
    folderId?: string;
    type: string;
    originName: string;
    name: string;
    mimetype?: string;
    extension?: string;
    relativePathSegments: string[];
    width?: number;
    height?: number;
    size?: number;
    url?: string;
    meta?: any;
}

export interface DigitaiUpdateFleetFileById
{
    id: string;
    companyId?: string;
    fleetId?: string;
    accountId?: string;
    folderId?: string;
    type?: string;
    originName?: string;
    name?: string;
    mimetype?: string;
    extension?: string;
    relativePathSegments?;
    width?: number;
    height?: number;
    size?: number;
    url?: string;
    meta?: any;
}

export interface DigitaiUploadFleetFilesInput
{
    fleetId: string;
    folderId: string;
}

export interface DigitaiUpdateFleetFiles
{
    id?: string;
    companyId?: string;
    fleetId?: string;
    accountId?: string;
    folderId?: string;
    type?: string;
    originName?: string;
    name?: string;
    mimetype?: string;
    extension?: string;
    relativePathSegments?: string[];
    width?: number;
    height?: number;
    size?: number;
    url?: string;
    meta?: any;
}

export interface DigitaiCreateFleetFileMessage
{
    id: string;
    fleetId: string;
    message: string;
}

export interface DigitaiCreateFleetMessage
{
    id: string;
    message: string;
}

export enum DigitaiFleetFileType
{
    FOLDER = 'FOLDER',
    FILE = 'FILE',
    SYMLINK = 'SYMLINK',
}

export interface DigitaiReasonAlert
{
    id: string;
    name: string;
    type: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateReasonAlert
{
    id: string;
    name: string;
    type: string;
}

export interface DigitaiUpdateReasonAlertById
{
    id: string;
    name?: string;
    type?: string;
}

export interface DigitaiUpdateReasonAlerts
{
    id?: string;
    name?: string;
    type?: string;
}

export interface DigitaiAlertLogReason
{
    id: string;
    name: string;
    type: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateAlertLogReason
{
    id: string;
    name: string;
    type: string;
}

export interface DigitaiUpdateAlertLogReasonById
{
    id: string;
    name?: string;
    type?: string;
}

export interface DigitaiUpdateAlertLogReasons
{
    id?: string;
    name?: string;
    type?: string;
}

export interface DigitaiTrackedIssue
{
    id: string;
    issueId: string;
    issue: DigitaiIssue;
    comment: string;
    isRecovering: boolean;
    isStable: boolean;
    isDeteriorating: boolean;
    followUp: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export enum TrackedIssueStatus
{
    RECOVERING = 'RECOVERING',
    STABLE = 'STABLE',
    DETERIORATING = 'DETERIORATING',
}

export interface DigitaiCreateTrackedIssue
{
    id: string;
    issueId: string;
    comment: string;
    isRecovering: boolean;
    isStable: boolean;
    isDeteriorating: boolean;
    followUp: string;
}

export interface DigitaiUpdateTrackedIssueById
{
    id: string;
    issueId?: string;
    comment?: string;
    isRecovering?: boolean;
    isStable?: boolean;
    isDeteriorating?: boolean;
    followUp?: string;
}

export interface DigitaiUpdateTrackedIssues
{
    id?: string;
    issueId?: string;
    comment?: string;
    isRecovering?: boolean;
    isStable?: boolean;
    isDeteriorating?: boolean;
    followUp?: string;
}

export interface DigitaiIdentifiedRootCause
{
    id: string;
    name: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateIdentifiedRootCause
{
    id: string;
    name: string;
}

export interface DigitaiUpdateIdentifiedRootCauseById
{
    id: string;
    name?: string;
}

export interface DigitaiUpdateIdentifiedRootCauses
{
    id?: string;
    name?: string;
}

export interface DigitaiFixAndValuation
{
    id: string;
    issueId: string;
    issue: DigitaiIssue;
    description: string;
    actionStatus?: string;
    findings?: string;
    effective?: boolean;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateFixAndValuation
{
    id: string;
    issueId: string;
    description: string;
    actionStatus?: string;
    findings?: string;
    effective?: boolean;
}

export interface DigitaiUpdateFixAndValuationById
{
    id: string;
    issueId?: string;
    description?: string;
    actionStatus?: string;
    findings?: string;
    effective?: boolean;
}

export interface DigitaiUpdateFixAndValuations
{
    id?: string;
    issueId?: string;
    description?: string;
    actionStatus?: string;
    findings?: string;
    effective?: boolean;
}

export interface DigitaiFleetTelemetryFile
{
    id: string;
    companyId: string;
    company: DigitaiCompany;
    fleetId: string;
    fleet: DigitaiFleet;
    accountId: string;
    account: IamAccount;
    folderId?: string;
    folder?: DigitaiFleetFile;
    type: string;
    originName: string;
    name: string;
    mimetype?: string;
    extension?: string;
    relativePathSegments;
    width?: number;
    height?: number;
    size?: number;
    url?: string;
    meta?: any;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiGenerateUploadSAS
{
    sasUrl: string;
}

export interface DigitaiGenerateUploadSASResponse
{
    digitaiGenerateUploadSAS: DigitaiGenerateUploadSAS;
}

export interface DigitaiUploadFilesFleetTelemetryFile
{
    digitaiUploadFilesFleetTelemetryFile: {
        numFiles: number;
        numFolders: number;
    };
}
export interface DigitaiCreateFleetTelemetryFile
{
    id: string;
    companyId: string;
    fleetId: string;
    accountId: string;
    folderId?: string;
    type: string;
    originName: string;
    name: string;
    mimetype?: string;
    extension?: string;
    relativePathSegments: string[];
    width?: number;
    height?: number;
    size?: number;
    url?: string;
    meta?: any;
}

export interface DigitaiUpdateFleetTelemetryFileById
{
    id: string;
    companyId?: string;
    fleetId?: string;
    accountId?: string;
    folderId?: string;
    type?: string;
    originName?: string;
    name?: string;
    mimetype?: string;
    extension?: string;
    relativePathSegments?;
    width?: number;
    height?: number;
    size?: number;
    url?: string;
    meta?: any;
}

export interface DigitaiMoveTempFilesToPermanentStorageInput
{
    uploadId: string;
    contextId: string;
    contextType: string;
    isDirectory: boolean;
}

export interface DigitaiUpdateFleetTelemetryFiles
{
    id?: string;
    companyId?: string;
    fleetId?: string;
    accountId?: string;
    folderId?: string;
    type?: string;
    originName?: string;
    name?: string;
    mimetype?: string;
    extension?: string;
    relativePathSegments?: string[];
    width?: number;
    height?: number;
    size?: number;
    url?: string;
    meta?: any;
}

export interface DigitaiUploadFleetTelemetryFilesInput
{
    fleetId: string;
    folderId: string;
}

export interface DigitaiOverviewGraph
{
    digitaiGetOverviewGraph: EChartsOption;
}

export interface DigitaiIssueOverviewGraph
{
    digitaiGetIssueOverviewGraph: EChartsOption;
}

export interface DigitaiChartVariable
{
    id: string;
    displayName: string;
    variableName: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateChartVariable
{
    id: string;
    displayName: string;
    variableName: string;
}

export interface DigitaiUpdateChartVariableById
{
    id: string;
    displayName?: string;
    variableName?: string;
}

export interface DigitaiUpdateChartVariables
{
    id?: string;
    displayName?: string;
    variableName?: string;
}

export interface DigitaiChartType
{
    id: string;
    value: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateChartType
{
    id: string;
    value: string;
}

export interface DigitaiUpdateChartTypeById
{
    id: string;
    value?: string;
}

export interface DigitaiUpdateChartTypes
{
    id?: string;
    value?: string;
}

export interface DigitaiEngineModelChartVariable
{
    engineModelId: string;
    engineModel: DigitaiEngineModel;
    chartVariableId: string;
    chartVariable: DigitaiChartVariable;
}

export interface DigitaiCreateEngineModelChartVariable
{
    engineModelId: string;
    chartVariableId: string;
}

export interface DigitaiUpdateEngineModelChartVariableById
{
    engineModelId?: string;
    chartVariableId?: string;
}

export interface DigitaiUpdateEngineModelsChartVariables
{
    engineModelId?: string;
    chartVariableId?: string;
}

export interface DigitaiIssueChartVariable
{
    issueId: string;
    issue: DigitaiIssue;
    chartVariableId: string;
    chartVariable: DigitaiChartVariable;
}

export interface DigitaiCreateIssueChartVariable
{
    issueId: string;
    chartVariableId: string;
}

export interface DigitaiUpdateIssueChartVariableById
{
    issueId?: string;
    chartVariableId?: string;
}

export interface DigitaiUpdateIssuesChartVariables
{
    issueId?: string;
    chartVariableId?: string;
}

export enum DigitContainerStorageAccount
{
    FILE_MANAGER = 'file-manager',
    TELEMETRY_MANAGER = 'telemetry-manager',
    ISSUE_MANAGER = 'issue-manager',
}

export interface DigitaiDynamicConfiguration
{
    id: string;
    field: string;
    sortField?: string;
    searchableField?: string;
    labelTranslationKey?: string;
    required?: boolean;
    inputType: string;
    options?: any;
    order?: number;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface DigitaiCreateDynamicConfiguration
{
    id: string;
    field: string;
    sortField?: string;
    searchableField?: string;
    labelTranslationKey?: string;
    required?: boolean;
    inputType: string;
    options?: any;
    order?: number;
}

export interface DigitaiUpdateDynamicConfigurationById
{
    id: string;
    field?: string;
    sortField?: string;
    searchableField?: string;
    labelTranslationKey?: string;
    required?: boolean;
    inputType?: string;
    options?: any;
    order?: number;
}

export interface DigitaiUpdateDynamicConfigurations
{
    id?: string;
    field?: string;
    sortField?: string;
    searchableField?: string;
    labelTranslationKey?: string;
    required?: boolean;
    inputType?: string;
    options?: any;
    order?: number;
}
